import { Box, Modal, Typography, Autocomplete, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { GeneralStatusEnum } from '../../../common/enums/common.enum';
import { theme } from '../../../theme';
import { LocationDto } from '../../../common/interfaces/kindergarten.interface';
import { updateLocation } from '../../../api/kindergarten.api';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import { getTimezones } from '../../../utilities/helper/timezone.helper';

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  kindergartenId: string;
  location?: LocationDto | null;
  onSuccess?: (updatedLocation: LocationDto) => void;
}
const LocationModal: React.FC<LocationModalProps> = ({ open, onClose, kindergartenId, location, onSuccess }) => {
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationTimezone, setLocationTimezone] = useState('');
  const [locationStatus, setLocationStatus] = useState(GeneralStatusEnum.ACTIVE);
  const [locationNameError, setLocationNameError] = useState(false);
  const [locationAddressError, setLocationAddressError] = useState(false);
  const [locationTimezoneError, setLocationTimezoneError] = useState(false);

  const timezones = useMemo(() => getTimezones(), []);

  useEffect(() => {
    if (location) {
      setLocationName(location.name || '');
      setLocationAddress(location.address || '');
      setLocationTimezone(location.timezone || '');
      setLocationStatus(location.status ?? GeneralStatusEnum.ACTIVE);
    }
    setLocationTimezoneError(false);
    setLocationNameError(false);
    setLocationAddressError(false);
  }, [location]);

  const handleSubmit = async () => {
    const isNameValid = locationName.trim() !== '';
    const isAddressValid = locationAddress.trim() !== '';
    const isTimezoneValid = locationTimezone.trim() !== '';

    setLocationNameError(!isNameValid);
    setLocationAddressError(!isAddressValid);
    setLocationTimezoneError(!isTimezoneValid);

    if (!isNameValid || !isAddressValid || !isTimezoneValid) {
      return;
    }
    try {
      const updatePayload = {
        name: locationName,
        address: locationAddress,
        timezone: locationTimezone,
        status: locationStatus
      };

      if (!location) {
        return;
      }
      const response = await updateLocation(kindergartenId, location.id, updatePayload);
      if (response?.results && onSuccess) {
        onSuccess(response.results);
        createNotification(MESSAGE_TYPE.SUCCESS, response?.message ?? 'Successfully updated location');
      }

      onClose();
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'An error occurred while updating the location');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          p: 3,
          borderRadius: 2,
          width: 520,
          outline: 'none',
          border: 'none'
        }}
      >
        <Typography variant="h6">Edit location</Typography>

        <Box
          sx={{
            display: 'flex',
            padding: 0,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '2px',
            width: '100%',
            alignSelf: 'stretch'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              width: '100%'
            }}
          >
            <GenericTextField
              label="Location name"
              value={locationName}
              onChange={e => {
                setLocationName(e.target.value);
                if (e.target.value.trim() !== '') setLocationNameError(false);
              }}
              required
              error={locationNameError}
            />
            <GenericTextField
              label="Location address"
              value={locationAddress}
              onChange={e => {
                setLocationAddress(e.target.value);
                if (e.target.value.trim() !== '') setLocationAddressError(false);
              }}
              multiline
              required
              error={locationAddressError}
              rows={3}
            />
            <Autocomplete
              fullWidth
              options={timezones}
              getOptionLabel={option => option.label}
              value={timezones.find(tz => tz.value === locationTimezone) || null}
              onChange={(_, newValue) => {
                setLocationTimezone(newValue?.value || '');
                if (newValue?.value?.trim() !== '') setLocationTimezoneError(false);
              }}
              renderInput={params => (
                <GenericTextField {...params} label="Timezone" placeholder="Search timezone..." required error={locationTimezoneError} />
              )}
              sx={{ width: '100%' }}
            />
          </Box>
        </Box>
        <GenericStatusSwitch label="Location status" status={locationStatus} onChange={newStatus => setLocationStatus(newStatus)} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            mt: 2,
            gap: 2
          }}
        >
          <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="text" onClick={onClose} label="Cancel" />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            isActive={locationName.trim() !== '' && locationAddress.trim() !== '' && locationTimezone.trim() !== ''}
            onClick={handleSubmit}
            label="Save"
          />
        </Box>
      </Box>
    </Modal>
  );
};
export default LocationModal;
